import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AttendeePanel = ({ attendees, onAddAttendee, onRemoveAttendee, canEdit }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAttendee, setNewAttendee] = useState({
    name: '',
    email: '',
    role: '',
    status: 'invited'
  });

  const roleOptions = [
    { value: 'organizer', label: 'Organizer' },
    { value: 'required', label: 'Required' },
    { value: 'optional', label: 'Optional' }
  ];

  const statusOptions = [
    { value: 'invited', label: 'Invited' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'declined', label: 'Declined' },
    { value: 'tentative', label: 'Tentative' }
  ];

  const handleAddAttendee = () => {
    if (newAttendee?.name && newAttendee?.email) {
      onAddAttendee(newAttendee);
      setNewAttendee({ name: '', email: '', role: '', status: 'invited' });
      setShowAddForm(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return { name: 'CheckCircle2', color: 'text-green-600' };
      case 'declined':
        return { name: 'XCircle', color: 'text-red-600' };
      case 'tentative':
        return { name: 'HelpCircle', color: 'text-yellow-600' };
      default:
        return { name: 'Clock', color: 'text-gray-600' };
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'organizer':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'required':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Users" size={20} className="text-primary" />
          <h3 className="font-semibold text-foreground">Attendees ({attendees?.length})</h3>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            Add
          </Button>
        )}
      </div>
      {showAddForm && (
        <div className="mb-4 p-3 bg-muted/30 rounded-lg border border-border">
          <Input
            label="Name"
            type="text"
            placeholder="Enter name"
            value={newAttendee?.name}
            onChange={(e) => setNewAttendee({ ...newAttendee, name: e?.target?.value })}
            className="mb-3"
          />
          <Input
            label="Email"
            type="email"
            placeholder="Enter email"
            value={newAttendee?.email}
            onChange={(e) => setNewAttendee({ ...newAttendee, email: e?.target?.value })}
            className="mb-3"
          />
          <Select
            label="Role"
            options={roleOptions}
            value={newAttendee?.role}
            onChange={(value) => setNewAttendee({ ...newAttendee, role: value })}
            placeholder="Select role"
            className="mb-3"
          />
          <div className="flex gap-2">
            <Button variant="default" size="sm" onClick={handleAddAttendee}>
              Add Attendee
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
        {attendees?.map((attendee) => {
          const statusIcon = getStatusIcon(attendee?.status);
          return (
            <div
              key={attendee?.id}
              className="flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-foreground truncate">{attendee?.name}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(attendee?.role)}`}>
                    {attendee?.role}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate">{attendee?.email}</p>
                <div className="flex items-center gap-1 mt-1">
                  <Icon name={statusIcon?.name} size={12} className={statusIcon?.color} />
                  <span className="text-xs text-muted-foreground capitalize">{attendee?.status}</span>
                </div>
              </div>
              {canEdit && attendee?.role !== 'organizer' && (
                <button
                  onClick={() => onRemoveAttendee(attendee?.id)}
                  className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label="Remove attendee"
                >
                  <Icon name="X" size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AttendeePanel;